import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { TabView } from 'primeng/tabview';
import { BusinessService } from '../../../services/business.service';
import { MenuService } from '../../../services/menu.service';
import { UserService } from '../../../services/user.service';
import { Constants } from '../../../model/common.model';
import { PanelModule } from '../../../modules/panel.module';
import { LocationSelectorComponent } from '../../location-selector/location-selector.component';
import { UiSettingsComponent } from '../../ui-settings/ui-settings.component';
import { LocationService } from '../../../services/location.service';
import { MapComponent } from '../../map/map.component';
import { InputGroupModule } from 'primeng/inputgroup';
import { TooltipModule } from 'primeng/tooltip';
import { UserAvatarComponent } from '../../user/user-avatar/user-avatar.component';

@Component({
  selector: 'brt-business-save',
  standalone: true,
  imports: [PanelModule, LocationSelectorComponent, UiSettingsComponent, MapComponent, InputGroupModule, UserAvatarComponent, TooltipModule],
  templateUrl: './business-save.component.html',
  styleUrl: './business-save.component.scss'
})
export class BusinessSaveComponent {
  basePath = this.route.snapshot.data['basePath'] || '/';
  constructor(private businessService: BusinessService, private router: Router, private route: ActivatedRoute,
    private userService: UserService, private messageService: MessageService, private locationService: LocationService,
    private menuService: MenuService) {
    this.loadHomepageCategories();
    this.route.params.subscribe(async params => {
      if (params['id'])
        this.business = await this.businessService.get(params['id']);
      if (this.business?.Id)
        this.loadMenuCategories(this.business.Id);
      if (!this.business.BankAccount) this.business.BankAccount = {};
      if (!Array.isArray(this.business.CategoryIds)) this.business.CategoryIds = [];
      if (!Array.isArray(this.business.PostcodeRules)) this.business.PostcodeRules = [];
      this.syncBusinessCoordinates();
      this.ensureDefaultEtaMinutes();
      this.ensurePaymentMethods();
      this.ensureOnlinePaymentFee();

      delete this.loading;
      setTimeout(() => {
        var hash = location.hash.replace('#', '');
        this.activeIndex = this.tabview?.tabs?.findIndex(t => t.header.replaceAll(' ', '').toLowerCase() == hash);
        if (!(this.activeIndex >= 0)) this.activeIndex = 0;
      });
    });

    userService.current$.subscribe(user => this.roles = user?.Permissions);
  }

  loading = true;
  now = new Date();
  roles: string[];
  get canManageBusiness() {
    return this.roles?.includes('manage-business') || this.roles?.includes('superadmin');
  }
  @ViewChild('tabview') tabview: TabView;
  activeIndex = 0;
  onTabChange(index: number) {
    this.activeIndex = index;
    location.hash = this.tabview.tabs[index].header.replaceAll(' ', '').toLowerCase();
  }

  permissions = Constants.BusinessPermissions;
  weekDays = Constants.WeekDays;
  orderTypes = Constants.OrderTypes;
  feeTypes = ['FixAmount', 'Percentage'];
  paymentMethods = ['Cash', 'Card'];
  business: any = {
    Staffs: [],
    OperatingHours: [{ OrderType: 'Default', Days: Constants.WeekDays, StartTime: '08:00:00', EndTime: '22:00:00' }],
    Location: {},
    DeliveryZones: [],
    PostcodeRules: [],
    ExtraFees: [],
    BankAccount: {},
    DefaultEtaMinutes: [{ OrderType: 'Default', Number: 0 }],
    CategoryIds: [],
    PaymentMethods: ['Cash', 'Card'],
    OnlinePaymentFee: 0.75,
    ShowPhoneOnBusinessPage: false,
    ShowBookTableOnBusinessPage: false
  };

  menuCategories: { Id: string; Name: string }[] = [];
  async loadMenuCategories(businessId: string) {
    const result = await this.menuService.searchMenu({ BusinessId: businessId });
    this.menuCategories = (result?.List || []).map((menu: any) => ({ Id: menu.Id, Name: menu.Name }));
  }

  categoryOptions: { label: string; value: string; image?: string }[] = [];
  async loadHomepageCategories() {
    const config = await this.businessService.getSystemConfig();
    const rawCategories = config?.UI?.HomeCategories;
    if (!rawCategories) return;

    try {
      const categories = JSON.parse(rawCategories);
      this.categoryOptions = (Array.isArray(categories) ? categories : [])
        .map(category => ({
          label: category.title || category.Title || category.name || category.Name || category.id || category.Id,
          value: category.id || category.Id || category.value || category.Value,
          image: category.image || category.Image
        }))
        .filter(category => category.label && category.value);
    } catch {
      this.categoryOptions = [];
    }
  }
  async save(navigate = true) {
    this.business.Domains = this.normalizeDomains(this.business.Domains);
    this.ensurePaymentMethods();
    this.ensureOnlinePaymentFee();
    this.loading = true;
    try {
      this.business = await this.businessService.save(this.business);
      this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Business data saved successfully' });
      if (navigate && this.basePath == '/')
        this.router.navigate(['/business']);
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Cannot save business', detail: err?.error?.message || err?.message || 'Failed to save business data' });
    } finally {
      delete this.loading;
    }
  }

  private normalizeDomains(domains: unknown): string[] {
    if (!Array.isArray(domains)) return [];
    return [...new Set(domains
      .filter((domain): domain is string => typeof domain === 'string')
      .map(domain => domain.trim().replace(/\.$/, '').toLowerCase())
      .filter(Boolean))];
  }

  users: any[];
  async searchUser(query: string) {
    this.users = (await this.userService.search({ Query: query })).List;
  }
  selectStaff(staff: any, user: any) {
    staff = Object.assign(staff, { UserId: user.Id, Profile: user.Profile });
  }

  suggestions: string[];
  async searchPostcode(query: string) {
    this.suggestions = await this.locationService.searchPostcode(query, true);
  }

  async stopOperatingUntil(until?: Date) {
    this.business.StopOpertingUntil = until;
    await this.save(false);
  }


  syncBusinessCoordinates() {
    this.business = this.business || {};
    this.business.Location = this.business.Location || {};

    const lat = this.business.Location.Latitude ?? this.business.Latitude;
    const lng = this.business.Location.Longitude ?? this.business.Longitude;
    const postcode = this.business.Location.Postcode ?? this.business.Postcode;

    if (lat !== undefined && lat !== null) {
      this.business.Location.Latitude = lat;
      this.business.Latitude = lat;
    }

    if (lng !== undefined && lng !== null) {
      this.business.Location.Longitude = lng;
      this.business.Longitude = lng;
    }

    if (postcode) {
      this.business.Location.Postcode = postcode;
      this.business.Postcode = postcode;
    }
  }

  private ensurePaymentMethods() {
    if (!Array.isArray(this.business.PaymentMethods))
      this.business.PaymentMethods = ['Cash', 'Card'];
  }

  private ensureOnlinePaymentFee() {
    this.business.OnlinePaymentFee ??= 0.75;
  }

  private ensureDefaultEtaMinutes() {
    if (!Array.isArray(this.business.DefaultEtaMinutes))
      this.business.DefaultEtaMinutes = [];

    const list = this.business.DefaultEtaMinutes;
    const defaultIndex = list.findIndex(item => item?.OrderType === 'Default');
    if (defaultIndex === -1)
      list.unshift({ OrderType: 'Default', Number: 0 });
    else if (defaultIndex > 0) {
      const [defaultItem] = list.splice(defaultIndex, 1);
      list.unshift(defaultItem);
    }
  }

  mapVisible: boolean;
  showMap(zone: any) {
    this.mapVisible = true;
  }

  onBusinessLocationSelected(event: { lat: number, lng: number, address?: string, postcode?: string }) {
    this.business.Location = this.business.Location || {};
    this.business.Location.Latitude = event.lat;
    this.business.Location.Longitude = event.lng;
    this.business.Latitude = event.lat;
    this.business.Longitude = event.lng;
    if (event.postcode) {
      this.business.Location.Postcode = event.postcode;
      this.business.Postcode = event.postcode;
    }
    if (event.address && !this.business.Location.AddressLines?.[0])
      this.business.Location.AddressLines = [event.address, ...(this.business.Location.AddressLines || []).slice(1)];
  }

  apiKey: string;
  async resetApiKey() {
    this.apiKey = await this.businessService.resetApiKey(this.business.Id);
    this.messageService.add({ severity: 'warn', summary: 'API Key Generated', detail: 'Copy this key now — it will not be shown again.' });
  }

  setupCode: string;
  setupCodeLoading = false;
  async generateSetupCode() {
    this.setupCodeLoading = true;
    try {
      const result = await this.businessService.generateSetupCode(this.business.Id);
      this.setupCode = result.code;
      this.apiKey = undefined;
      this.messageService.add({ severity: 'info', summary: 'Setup Code Generated', detail: 'Enter this 6-digit code in the BritLink Android app. Valid for 24 hours.' });
    } finally {
      this.setupCodeLoading = false;
    }
  }

  copyToClipboard(value: string) {
    navigator.clipboard.writeText(value);
    this.messageService.add({ severity: 'success', summary: 'Copied', detail: 'Copied to clipboard' });
  }

  // ---- Per-postcode delivery rules ----
  // Rules override the business-wide min order / delivery charge / free-over threshold for a
  // specific postcode. They can be entered by hand or imported from a "Post Codes" Excel file
  // with columns: Post Code, Min Order, Delivery Charge, Delivery Free if order over.
  // A "Delivery Charge" cell of "Excluded from Delivery" flags the postcode as excluded.

  addPostcodeRule() {
    if (!Array.isArray(this.business.PostcodeRules)) this.business.PostcodeRules = [];
    this.business.PostcodeRules.unshift({ Postcode: '', MinOrderAmount: null, DeliveryCharge: null, FreeDeliveryOverAmount: null, Excluded: false });
  }

  removePostcodeRule(index: number) {
    this.business.PostcodeRules.splice(index, 1);
  }

  clearPostcodeRules() {
    this.business.PostcodeRules = [];
  }

  private normalizePostcode(value: any): string {
    return (value == null ? '' : String(value)).replace(/\s+/g, '').toUpperCase();
  }

  private parseAmount(value: any): number | null {
    if (value == null || value === '') return null;
    const n = typeof value === 'number' ? value : parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }

  importingPostcodes = false;
  postcodeImportSummary: string;
  async importPostcodeExcel(event: any, fileUpload: any) {
    const file = event.files?.[0];
    if (!file) return;
    this.importingPostcodes = true;
    this.postcodeImportSummary = undefined;
    try {
      const XLSX = await import('xlsx');
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: any[] = XLSX.utils.sheet_to_json(sheet, { defval: null });

      // Tolerant column lookup: match by the collapsed, lower-cased header text.
      const pick = (row: any, ...needles: string[]) => {
        for (const key of Object.keys(row)) {
          const norm = key.toLowerCase().replace(/\s+/g, '');
          if (needles.every(n => norm.includes(n))) return row[key];
        }
        return null;
      };

      const rules: any[] = [];
      let excludedCount = 0;
      for (const row of rows) {
        const postcode = (pick(row, 'postcode') ?? pick(row, 'post', 'code'));
        if (postcode == null || String(postcode).trim() === '') continue;
        const chargeRaw = pick(row, 'delivery', 'charge');
        const excluded = typeof chargeRaw === 'string' && /exclud/i.test(chargeRaw);
        if (excluded) excludedCount++;
        rules.push({
          Postcode: String(postcode).trim().toUpperCase(),
          Excluded: excluded,
          MinOrderAmount: excluded ? null : this.parseAmount(pick(row, 'min', 'order')),
          DeliveryCharge: excluded ? null : this.parseAmount(chargeRaw),
          FreeDeliveryOverAmount: excluded ? null : this.parseAmount(pick(row, 'free', 'over'))
        });
      }

      if (!rules.length) {
        this.messageService.add({ severity: 'warn', summary: 'Nothing imported', detail: 'No postcode rows were found in that file. Expected a "Post Code" column.' });
        return;
      }

      // Merge with existing rules, replacing any rule for the same postcode.
      const existing: any[] = Array.isArray(this.business.PostcodeRules) ? this.business.PostcodeRules : [];
      const byPostcode = new Map<string, any>();
      for (const r of existing) byPostcode.set(this.normalizePostcode(r.Postcode), r);
      let updated = 0, added = 0;
      for (const r of rules) {
        const key = this.normalizePostcode(r.Postcode);
        if (byPostcode.has(key)) updated++; else added++;
        byPostcode.set(key, r);
      }
      this.business.PostcodeRules = [...byPostcode.values()].sort((a, b) => this.normalizePostcode(a.Postcode).localeCompare(this.normalizePostcode(b.Postcode)));

      this.postcodeImportSummary = `Imported ${rules.length} postcode${rules.length === 1 ? '' : 's'} (${added} added, ${updated} updated, ${excludedCount} excluded). Remember to Save.`;
      this.messageService.add({ severity: 'success', summary: 'Excel imported', detail: this.postcodeImportSummary });
    } catch (err: any) {
      this.messageService.add({ severity: 'error', summary: 'Import failed', detail: err?.message || 'Could not read that Excel file.' });
    } finally {
      fileUpload?.clear?.();
      this.importingPostcodes = false;
    }
  }
}
